import styled from 'styled-components';
import { AppTheme } from '../../settings/appTheme';

/** Hero assets share the same canvas (902×813); keeps layout stable before decode. */
export const REFLINK_STEP_1_SRC = 'content/img/reflink/step_1.png';
export const REFLINK_STEP_2_SRC = 'content/img/reflink/step_2.png';

export function preloadReflinkImage(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const finish = () => resolve();
    img.onload = finish;
    img.onerror = () => reject(new Error(`Failed to load ${src}`));
    img.src = src;
    if (img.complete && img.naturalWidth > 0) {
      finish();
    }
  });
}

/** Warm cache when the reflink overlay opens (direct links to step 2, back/forward). */
export function preloadReflinkHeroImages(): void {
  void preloadReflinkImage(REFLINK_STEP_1_SRC);
  void preloadReflinkImage(REFLINK_STEP_2_SRC);
}

export const ReflinkPageRoot = styled.div`
  flex: 1;
  width: 100%;
  padding: 24px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ReflinkCard = styled.div`
  width: min(100%, 830px);
`;

export const ReflinkFrame = styled.div`
  position: relative;
  width: 100%;
  container-type: inline-size;
  container-name: reflink;
`;

export const ReflinkActions = styled.div<{ $translateY?: string; $desktopTranslateY?: string; $mobileTranslateY?: string }>`
  position: absolute;
  left: 50%;
  transform: translate(-50%, ${(p) => p.$translateY ?? '0px'});
  width: min(62%, 520px);
  max-width: calc(100% - 32px);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: clamp(8px, 2.6cqi, 16px);
  z-index: 2;

  @media (min-width: 641px) {
    transform: translate(-50%, ${(p) => p.$desktopTranslateY ?? p.$translateY ?? '0px'});
    width: min(88%, 360px);
  }

  @media (max-width: 640px) {
    transform: translate(-50%, ${(p) => p.$mobileTranslateY ?? p.$translateY ?? '0px'});
    width: min(86%, 520px);
    gap: clamp(8px, 2.2vw, 16px);
  }

  @media (max-width: 380px) {
    width: min(92%, 520px);
  }
`;

export const ReflinkHeroFrame = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 902 / 813;
`;

export const ReflinkHeroImg = styled.img`
  display: block;
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 28px;
  border: none;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);

  @media (max-width: 640px) {
    border-radius: 20px;
  }
`;

export const ReflinkCloseButton = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  width: 42px;
  height: 42px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: rgba(0, 0, 0, 0.5);
  color: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
  font-family: ${AppTheme.fonts.regularFont};
  font-size: 26px;
  line-height: 1;

  &:hover {
    background: rgba(0, 0, 0, 0.62);
  }

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.18);
  }

  @media (max-width: 640px) {
    top: 10px;
    right: 10px;
    width: 38px;
    height: 38px;
    font-size: 24px;
  }
`;

export const ReflinkCtaButton = styled.button`
  width: 100%;
  min-height: clamp(36px, 10cqi, 52px);
  padding: clamp(8px, 2.2cqi, 12px) clamp(12px, 3.8cqi, 18px);
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 999px;
  background: ${AppTheme.colors.accent};
  color: #ffffff;
  font-family: ${AppTheme.fonts.regularFont};
  font-weight: 400;
  font-size: clamp(11px, 4.2cqi, 18px);
  letter-spacing: 0.03em;
  cursor: pointer;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.32);
  line-height: 1.15;
  text-align: center;
  white-space: normal;

  @media (max-width: 640px) {
    min-height: clamp(40px, 11vw, 54px);
    padding: 10px 16px;
    font-size: clamp(12px, 3.6vw, 18px);
  }

  @media (max-width: 380px) {
    font-size: clamp(12px, 3.8vw, 17px);
  }

  &:hover {
    filter: brightness(1.03);
  }

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.24), 0 12px 28px rgba(0, 0, 0, 0.32);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
`;

export const ReflinkCtaButtonCompact = styled(ReflinkCtaButton)`
  width: min(88%, 360px);
  align-self: center;
  min-height: clamp(32px, 9.5cqi, 44px);
  padding: clamp(6px, 1.8cqi, 10px) clamp(10px, 3.2cqi, 16px);
  font-size: clamp(11px, 3.8cqi, 15px);
  white-space: nowrap;

  @media (min-width: 641px) {
    width: min(92%, 340px);
  }

  @media (max-width: 640px) {
    width: min(78%, 520px);
    min-height: clamp(34px, 9vw, 44px);
    padding: 8px 14px;
    font-size: clamp(12px, 3.6vw, 15px);
  }

  @media (max-width: 380px) {
    width: min(92%, 520px);
    font-size: clamp(12px, 3.6vw, 16px);
  }
`;

export const ReflinkJoinLink = styled.button`
  align-self: flex-end;
  max-width: 100%;
  border: none;
  background: rgba(0, 0, 0, 0.42);
  color: rgba(255, 255, 255, 0.92);
  font-family: ${AppTheme.fonts.lightFont};
  font-weight: 300;
  font-size: clamp(11px, 4cqi, 16px);
  letter-spacing: 0.05em;
  cursor: pointer;
  padding: clamp(7px, 2cqi, 11px) clamp(10px, 2.8cqi, 15px);
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: clamp(5px, 1.4cqi, 9px);
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.28);
  backdrop-filter: blur(2px);
  line-height: 1.1;
  white-space: nowrap;
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 0.25em;

  &::after {
    content: '';
    width: clamp(18px, 5cqi, 24px);
    height: clamp(18px, 5cqi, 24px);
    background: url('content/img/reflink/icLogoMosquito.png') center / contain no-repeat;
    flex: 0 0 auto;
  }

  @media (max-width: 640px) {
    font-size: clamp(12px, 3.5vw, 18px);
    padding: clamp(9px, 2.4vw, 12px) clamp(12px, 3.2vw, 16px);
    gap: clamp(6px, 1.6vw, 10px);

    &::after {
      width: clamp(20px, 5.2vw, 26px);
      height: clamp(20px, 5.2vw, 26px);
    }
  }

  @media (max-width: 380px) {
    font-size: clamp(12px, 3.6vw, 16px);
  }

  &:hover {
    color: rgba(255, 255, 255, 1);
    background: rgba(0, 0, 0, 0.5);
  }

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.14), 0 10px 22px rgba(0, 0, 0, 0.28);
  }
`;

export const ReflinkFieldLabel = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 70%;
  text-align: center;
  color: rgba(255, 255, 255, 0.92);
  font-family: ${AppTheme.fonts.regularFont};
  font-size: clamp(12px, 1.7vw, 18px);
`;

export const ReflinkEmailInput = styled.input`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 44.2%;
  width: 70%;

  @media (min-width: 641px) {
    top: 41%;
  }
  height: clamp(32px, 9cqi, 42px);
  border-radius: 999px;
  border: 1px solid rgba(255, 191, 0, 0.8);
  background: rgba(0, 0, 0, 0.18);
  color: rgba(255, 255, 255, 0.94);
  padding: 0 clamp(10px, 3.2cqi, 16px);
  font-family: ${AppTheme.fonts.regularFont};
  font-size: clamp(11px, 3.6cqi, 15px);
  text-align: center;

  @media (max-width: 640px) {
    height: clamp(34px, 9vw, 44px);
    font-size: clamp(12px, 3.4vw, 16px);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.55);
  }

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 4px rgba(255, 191, 0, 0.18);
  }
`;

export const ReflinkLinkValue = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 78%;
  padding: 10px 14px;
  border-radius: 18px;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.92);
  font-family: ${AppTheme.fonts.regularFont};
  font-size: clamp(11px, 1.4vw, 14px);
  line-height: 1.25;
  text-align: center;
  word-break: break-all;
`;

/** Shown in the same spot as the email field after the link is generated */
export const ReflinkLinkDisplay = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 44.2%;
  width: 70%;

  @media (min-width: 641px) {
    top: 41%;
  }
  min-height: clamp(32px, 9cqi, 42px);
  border-radius: 22px;
  border: 1px solid rgba(255, 191, 0, 0.85);
  background: rgba(0, 0, 0, 0.42);
  color: rgba(255, 255, 255, 0.95);
  font-family: ${AppTheme.fonts.regularFont};
  font-size: clamp(10px, 3.2cqi, 13px);
  line-height: 1.35;
  text-align: center;
  word-break: break-all;
  padding: clamp(8px, 2.2cqi, 12px) clamp(10px, 3cqi, 14px);
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: all;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.06);

  @media (max-width: 640px) {
    min-height: clamp(34px, 9vw, 44px);
    font-size: clamp(11px, 3.2vw, 14px);
    padding: 10px 14px;
  }
`;

